import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import toast from "react-hot-toast";
import { FormField, FormRow, FormToggle } from "../../../components/ui";
import {
  MAX_POINT_RULE_VALUE,
  MIN_POINT_RULE_VALUE,
  POINT_RULE_ICON_OPTIONS,
  PointUnitName,
} from "../../../models/pointRule";
import {
  useGetPointStreakQuery,
  useSavePointStreakMutation,
} from "../../../store/services/point-rules";
import { getApiErrorMessage } from "../../../utils/functions/api-errors";
import {
  STREAK_MAX_WEEKS,
  StreakFormErrors,
  StreakFormValues,
  buildInitialStreakForm,
  buildSavePointStreakPayload,
  canAddStreakMilestone,
  describeStreakMilestone,
  hasStreakFormErrors,
  isStreakIntervalLocked,
  isStreakIntervalValid,
  streakMilestoneTitle,
  streakMilestoneWeeks,
  validateStreakForm,
  withNextMilestone,
  withoutLastNewMilestone,
} from "./pointStreakForm";
import { PointRulePreview } from "./PointRulePreview";

const HOW_IT_WORKS =
  "La racha suma 1 cada semana (de lunes a domingo) en la que el usuario asiste al menos a un evento. Si una semana no asiste, vuelve a 0 y empieza de nuevo desde el primer hito. Solo pagan los hitos.";
const INTERVAL_LOCKED_HINT = "Ya se pagaron hitos: el intervalo no se puede cambiar.";
type PointRuleIconOption = (typeof POINT_RULE_ICON_OPTIONS)[number];

const renderIconOption = (option: PointRuleIconOption) => (
  <span className="flex align-items-center gap-2">
    <i className={option.icon} />
    {option.label}
  </span>
);

const formatPreviewPoints = (points: number | null, unitName: PointUnitName): string =>
  points === null
    ? "Sin configurar"
    : `+${points} ${points === 1 ? unitName.singular : unitName.plural}`;

const INTERVAL_FIRST_MESSAGE =
  "Completá cada cuántas semanas se cumple un hito para configurar cuánto paga cada etapa.";

const NOT_CONFIGURED_MESSAGE = "La racha todavía no está configurada. La configura el equipo de GRUPS.";

export interface PointStreakEditor {
  form: StreakFormValues;
  setForm: Dispatch<SetStateAction<StreakFormValues>>;
  errors: StreakFormErrors;
  intervalLocked: boolean;
  /** TENANT_ADMIN sin racha configurada: no hay nada que editar. */
  notConfigured: boolean;
  isLoading: boolean;
  loadError: string | null;
  saving: boolean;
  /** Valida y guarda la racha completa; true si se guardó. */
  save: () => Promise<boolean>;
  /** Errores para el resumen junto a los botones, una vez que se intentó guardar. */
  errorMessages: string[];
}

/**
 * Estado de la configuración de la racha semanal del tenant (intervalo, textos y puntos de cada
 * hito). Arranca de lo guardado cada vez que se habilita, y no se pisa si la consulta se refresca
 * mientras se edita.
 */
export const usePointStreakEditor = (
  tenantId: string,
  enabled: boolean,
  canEditStructure: boolean
): PointStreakEditor => {
  const {
    data: config,
    isLoading,
    isError,
    error,
  } = useGetPointStreakQuery(tenantId, {
    skip: !enabled,
  });
  const [savePointStreak, saveState] = useSavePointStreakMutation();
  const [form, setForm] = useState<StreakFormValues>(() => buildInitialStreakForm(undefined));
  const [attempted, setAttempted] = useState(false);
  const seeded = useRef(false);

  useEffect(() => {
    if (!enabled) {
      seeded.current = false;
      return;
    }
    if (seeded.current || isLoading) return;
    seeded.current = true;
    setForm(buildInitialStreakForm(config));
    setAttempted(false);
  }, [enabled, config, isLoading]);

  const errors = validateStreakForm(form);

  const save = async (): Promise<boolean> => {
    setAttempted(true);
    if (hasStreakFormErrors(errors)) return false;
    const payload = buildSavePointStreakPayload(form, tenantId);
    if (payload === null) return false;
    try {
      await savePointStreak(payload).unwrap();
      toast.success("Racha guardada. Los cambios no son retroactivos.");
      return true;
    } catch (saveError) {
      toast.error(getApiErrorMessage(saveError) ?? "No se pudo guardar la racha");
      return false;
    }
  };

  return {
    form,
    setForm,
    errors: attempted ? errors : {},
    intervalLocked: isStreakIntervalLocked(form),
    notConfigured: !canEditStructure && (config?.milestones.length ?? 0) === 0,
    isLoading,
    loadError: isError ? (getApiErrorMessage(error) ?? "No se pudo cargar la racha.") : null,
    saving: saveState.isLoading,
    save,
    errorMessages: attempted
      ? [
          ...(errors.displayTitle ? [errors.displayTitle] : []),
          ...(errors.intervalWeeks ? [errors.intervalWeeks] : []),
          ...Object.values(errors.milestones ?? {}),
        ]
      : [],
  };
};

export interface PointStreakFieldsProps {
  editor: PointStreakEditor;
  /** El intervalo y los hitos nuevos son de SUPER_ADMIN; TENANT_ADMIN edita puntos y estado. */
  canEditStructure: boolean;
}

/** Campos de la racha: textos, intervalo y puntos de cada etapa (N, 2N, 3N… semanas). */
export const PointStreakFields = ({ editor, canEditStructure }: PointStreakFieldsProps) => {
  const { form, setForm, intervalLocked } = editor;
  const shownErrors = editor.errors;
  const lastIndex = form.milestones.length - 1;
  const nextWeeks = streakMilestoneWeeks(form.intervalWeeks, form.milestones.length);

  const setMilestone = (index: number, changes: Partial<StreakFormValues["milestones"][number]>) =>
    setForm((previous) => ({
      ...previous,
      milestones: previous.milestones.map((milestone, current) =>
        current === index ? { ...milestone, ...changes } : milestone
      ),
    }));

  if (editor.isLoading) {
    return <Message severity="info" text="Cargando la racha…" className="w-full" />;
  }
  if (editor.loadError !== null) {
    return <Message severity="error" text={editor.loadError} className="w-full" />;
  }
  if (editor.notConfigured) {
    return <Message severity="info" text={NOT_CONFIGURED_MESSAGE} className="w-full" />;
  }

  return (
    <>
      <Message severity="info" text={HOW_IT_WORKS} className="w-full mb-3" />

      <FormRow>
        <FormField
          label="Título en la app"
          htmlFor="pointStreakTitle"
          required={form.visibleToUser}
          hint="Cada hito le suma sus semanas: «Racha de asistencia · 4 semanas»."
          error={shownErrors.displayTitle}
        >
          <InputText
            id="pointStreakTitle"
            placeholder="Ej: Racha de asistencia"
            value={form.displayTitle}
            maxLength={200}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                displayTitle: event.target.value,
              }))
            }
            className="w-full"
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField label="Descripción en la app" htmlFor="pointStreakDescription">
          <InputTextarea
            id="pointStreakDescription"
            placeholder="Ej: Asistí al menos a un evento por semana para mantener tu racha."
            value={form.displayDescription}
            maxLength={200}
            rows={3}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                displayDescription: event.target.value,
              }))
            }
            className="w-full"
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField label="Ícono" htmlFor="pointStreakIcon">
          <Dropdown
            appendTo="self"
            inputId="pointStreakIcon"
            value={form.iconKey}
            options={POINT_RULE_ICON_OPTIONS}
            optionLabel="label"
            optionValue="key"
            placeholder="Elegí un ícono"
            showClear
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                iconKey: event.value ?? null,
              }))
            }
            itemTemplate={renderIconOption}
            valueTemplate={(option: PointRuleIconOption | null, props) =>
              option ? renderIconOption(option) : <span>{props.placeholder}</span>
            }
            className="w-full"
          />
        </FormField>
      </FormRow>

      <FormRow>
        <FormField
          label="Cada cuántas semanas seguidas se cumple un hito de racha"
          htmlFor="pointStreakInterval"
          required
          hint={
            intervalLocked
              ? INTERVAL_LOCKED_HINT
              : "Ej: con 3, los hitos se pagan a las 3, 6 y 9 semanas seguidas."
          }
          error={
              shownErrors.intervalWeeks ??
              (form.intervalWeeks !== null && !isStreakIntervalValid(form.intervalWeeks)
                ? `Ingresá un número entero entre 1 y ${STREAK_MAX_WEEKS}`
                : undefined)
            }
        >
          <InputNumber
            inputId="pointStreakInterval"
            placeholder="Ej: 3"
            value={form.intervalWeeks}
            onValueChange={(event) =>
              setForm((previous) => ({
                ...previous,
                intervalWeeks: event.value ?? null,
              }))
            }
            min={1}
            max={STREAK_MAX_WEEKS}
            disabled={!canEditStructure || intervalLocked}
            useGrouping={false}
            maxFractionDigits={0}
            className="w-full"
          />
        </FormField>
      </FormRow>

      {/* Las etapas dependen del intervalo: sin él no se sabe a cuántas semanas paga cada una. */}
      {!isStreakIntervalValid(form.intervalWeeks) ? (
        <Message severity="info" text={INTERVAL_FIRST_MESSAGE} className="w-full mb-3" />
      ) : (
        <>
          {form.milestones.map((milestone, index) => (
            <FormRow key={milestone.id ?? `new-${index}`}>
              <FormField
                label={
                  form.intervalWeeks === null
                    ? `Puntos del hito ${index + 1}`
                    : `${streakMilestoneWeeks(form.intervalWeeks, index)} semanas seguidas · paga`
                }
                htmlFor={`pointStreakMilestone${index}`}
                required
                hint={describeStreakMilestone(form.intervalWeeks, index, index === lastIndex)}
                error={shownErrors.milestones?.[index]}
              >
                {/* El switch va en la línea del campo, no a la altura del título. */}
                <div className="flex align-items-center gap-3">
                  <InputNumber
                    inputId={`pointStreakMilestone${index}`}
                    placeholder={`Ej: ${(index + 1) * 50}`}
                    value={milestone.pointsValue}
                    onValueChange={(event) =>
                      setMilestone(index, { pointsValue: event.value ?? null })
                    }
                    min={MIN_POINT_RULE_VALUE}
                    max={MAX_POINT_RULE_VALUE}
                    useGrouping={false}
                    maxFractionDigits={0}
                    className="flex-1"
                    inputClassName="w-full"
                  />
                  <span className="flex align-items-center gap-2">
                    <InputSwitch
                      inputId={`pointStreakMilestoneActive${index}`}
                      checked={milestone.active}
                      onChange={(event) => setMilestone(index, { active: event.value })}
                    />
                    <label htmlFor={`pointStreakMilestoneActive${index}`} className="text-sm m-0">
                      Activo
                    </label>
                  </span>
                </div>
              </FormField>
            </FormRow>
          ))}

          {canEditStructure && (
            <div className="flex flex-wrap gap-2 mb-3">
              <Button
                type="button"
                icon="pi pi-plus"
                label={nextWeeks === null ? "Agregar hito" : `Agregar hito de ${nextWeeks} semanas`}
                outlined
                size="small"
                disabled={!canAddStreakMilestone(form)}
                onClick={() => setForm(withNextMilestone)}
              />
              {form.milestones[lastIndex]?.id === null && form.milestones.length > 1 && (
                <Button
                  type="button"
                  icon="pi pi-minus"
                  label="Quitar el último"
                  outlined
                  severity="secondary"
                  size="small"
                  onClick={() => setForm(withoutLastNewMilestone)}
                />
              )}
            </div>
          )}

          <FormRow>
            <FormToggle
              label="Visible en la app"
              htmlFor="pointStreakVisible"
              hint="Apagado, los hitos pagan igual pero no aparecen en la app."
            >
              <InputSwitch
                inputId="pointStreakVisible"
                checked={form.visibleToUser}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    visibleToUser: event.value,
                  }))
                }
              />
            </FormToggle>
          </FormRow>
        </>
      )}
    </>
  );
};

/** Cómo se ve el primer hito en la app. */
export const PointStreakPreview = ({
  form,
  unitName,
}: {
  form: StreakFormValues;
  unitName: PointUnitName;
}) => (
  <PointRulePreview
    actionType="streak"
    iconKey={form.iconKey}
    title={streakMilestoneTitle(form.displayTitle, streakMilestoneWeeks(form.intervalWeeks, 0))}
    description={form.displayDescription.trim() === "" ? null : form.displayDescription}
    valueLabel={formatPreviewPoints(form.milestones[0]?.pointsValue ?? null, unitName)}
    isPenalty={false}
    visibleToUser={form.visibleToUser}
  />
);
